/**
 * Candidate Inquiry Chat Component
 * Allows users to ask questions about candidates (max 4 per candidate)
 * Simulates a phone screen / candidate inquiry
 */

import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import {
    MessageSquare,
    Send,
    Loader2,
    Phone,
    HelpCircle,
    X,
    ChevronDown,
    ChevronUp
} from 'lucide-react';

// Suggested questions based on common recruiter queries
const SUGGESTED_QUESTIONS = [
    "Why are you looking to leave your current role?",
    "Can you tell me more about your key projects?",
    "What's your expected salary range?",
    "What's your notice period?",
    "What kind of role are you looking for?",
    "Tell me about a challenging project you worked on.",
    "What are your career goals for the next 3-5 years?",
    "Are you open to relocation?",
    "What's your experience with [specific skill]?",
    "Why did you leave your previous company?"
];

const CandidateInquiryChat = ({ 
    sessionId, 
    candidate, 
    api, 
    onInquiryComplete,
    expanded: initialExpanded = false 
}) => {
    const [expanded, setExpanded] = useState(initialExpanded);
    const [inquiries, setInquiries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [question, setQuestion] = useState('');
    const [remaining, setRemaining] = useState(4);
    const [showSuggestions, setShowSuggestions] = useState(true);
    const scrollRef = useRef(null);

    const MAX_INQUIRIES = 4;

    // Fetch existing inquiries when expanded
    useEffect(() => {
        if (expanded && candidate) {
            fetchInquiries();
        }
    }, [expanded, candidate?.id]);

    // Scroll to bottom when new inquiry added
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [inquiries]);

    const fetchInquiries = async () => {
        try {
            const res = await api.get(`/sourcing-labs/sessions/${sessionId}/candidates/${candidate.id}/inquiries`);
            setInquiries(res.data.inquiries || []);
            setRemaining(res.data.remaining || 0);
        } catch (err) {
            console.error('Failed to fetch inquiries:', err);
        }
    };

    const submitQuestion = async (q = null) => {
        const questionText = q || question.trim();
        if (!questionText || remaining <= 0) return;

        setLoading(true);
        setQuestion('');
        setShowSuggestions(false);

        try {
            const res = await api.post(
                `/sourcing-labs/sessions/${sessionId}/candidates/${candidate.id}/inquiry`,
                { question: questionText }
            );

            // Add to local state
            setInquiries(prev => [...prev, {
                question: questionText,
                response: res.data.response,
                inquiry_number: res.data.inquiry_number
            }]);
            setRemaining(res.data.remaining_inquiries);

            if (res.data.remaining_inquiries === 0) {
                toast.info('You have used all questions for this candidate');
            }

            // Callback for parent component
            if (onInquiryComplete) {
                onInquiryComplete(candidate.id, inquiries.length + 1);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to submit question');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submitQuestion();
        }
    };

    if (!candidate) return null;

    return (
        <Card className={`transition-all ${expanded ? 'border-primary/50' : ''}`}>
            <CardHeader 
                className="cursor-pointer hover:bg-muted/30 transition-colors p-3"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-primary" />
                        <CardTitle className="text-sm font-medium">
                            Ask {candidate.name}
                        </CardTitle>
                        <Badge variant="outline" className="text-xs">
                            {remaining}/{MAX_INQUIRIES} questions left
                        </Badge>
                    </div>
                    {expanded ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                </div>
            </CardHeader>

            {expanded && (
                <CardContent className="p-3 pt-0 space-y-3">
                    {/* Info Banner */}
                    <div className="text-xs text-muted-foreground bg-muted/30 p-2 rounded-lg">
                        <p className="flex items-center gap-1">
                            <HelpCircle className="h-3 w-3" />
                            Simulate a phone screen - ask questions to better understand this candidate
                        </p>
                    </div>

                    {/* Chat Messages */}
                    <ScrollArea className="h-[200px] pr-2" ref={scrollRef}>
                        <div className="space-y-3">
                            {inquiries.length === 0 && showSuggestions && (
                                <div className="space-y-2">
                                    <p className="text-xs text-muted-foreground">Suggested questions:</p>
                                    <div className="flex flex-wrap gap-1">
                                        {SUGGESTED_QUESTIONS.slice(0, 4).map((q, idx) => (
                                            <Button
                                                key={idx}
                                                variant="outline"
                                                size="sm"
                                                className="text-xs h-auto py-1 px-2"
                                                onClick={() => submitQuestion(q)}
                                                disabled={loading || remaining <= 0}
                                            >
                                                {q.slice(0, 35)}...
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {inquiries.map((inq, idx) => (
                                <div key={idx} className="space-y-2">
                                    {/* Question */}
                                    <div className="flex justify-end">
                                        <div className="bg-primary text-primary-foreground rounded-lg p-2 max-w-[80%]">
                                            <p className="text-sm">{inq.question}</p>
                                        </div>
                                    </div>
                                    {/* Response */}
                                    <div className="flex justify-start">
                                        <div className="bg-muted rounded-lg p-2 max-w-[85%]">
                                            <p className="text-sm">{inq.response}</p>
                                            <p className="text-[10px] text-muted-foreground mt-1">
                                                — {candidate.name}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {loading && (
                                <div className="flex justify-start">
                                    <div className="bg-muted rounded-lg p-2 flex items-center gap-2">
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                        <span className="text-sm text-muted-foreground">
                                            {candidate.name} is typing...
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </ScrollArea>

                    {/* Input */}
                    {remaining > 0 ? (
                        <div className="flex gap-2">
                            <Input
                                placeholder={`Ask ${candidate.name} a question...`}
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                onKeyDown={handleKeyDown}
                                disabled={loading}
                                className="text-sm"
                            />
                            <Button 
                                size="icon" 
                                onClick={() => submitQuestion()}
                                disabled={loading || !question.trim()}
                            >
                                {loading ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Send className="h-4 w-4" />
                                )}
                            </Button>
                        </div>
                    ) : (
                        <div className="text-center py-2">
                            <Badge variant="secondary" className="text-xs">
                                All questions used for this candidate
                            </Badge>
                        </div>
                    )}

                    {/* Inquiry Count */}
                    {inquiries.length > 0 && (
                        <div className="flex justify-center">
                            <span className="text-[10px] text-muted-foreground">
                                {inquiries.length} of {MAX_INQUIRIES} questions asked
                            </span>
                        </div>
                    )}
                </CardContent>
            )}
        </Card>
    );
};

export default CandidateInquiryChat;
